import { useState } from "react";
import { useShow } from "@refinedev/core";
import { Show } from "@refinedev/antd";
import { Card, Descriptions, Tag, Button, Space, Tabs } from "antd";
import { EditOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import type { IncidenceDetail } from "../../types";
import dayjs from "dayjs";
import { AuditInfoPanel, IncidenceComments, ChangeStatusModal } from "./components";
import { usePageTitle } from "../../hooks";


/**
 * IncidenceDetail Component
 * Displays detailed information about a specific incidence with tabs for:
 * - General Information
 * - Audit Information
 * - Comments
 */
export const IncidenceShow = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [statusModalVisible, setStatusModalVisible] = useState(false);

  // After unification, audit incidences live on the Ticket model.
  // The detail serializer exposes the legacy `num_incidence /
  // status_incidence / kind_incidence_*` aliases so the rest of
  // this page renders without further changes.
  const { query } = useShow<IncidenceDetail>({
    resource: "v1/tickets",
  });

  const { data, isLoading } = query;
  const record = data?.data;
  
  // Update page title with incidence number
  const pageTitle = record 
    ? `Incidencia ${record.num_incidence}`
    : "Detalle Incidencia";
  usePageTitle(pageTitle);

  // Format number according to current locale
  const formatNumber = (value: number | null | undefined, decimals: number = 2): string => {
    if (value === null || value === undefined) return "0";
    return value.toLocaleString(i18n.language, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  // Get status display text (translated)
  const getStatusDisplay = (status: number): string => {
    switch (status) {
      case 1:
        return t("incidence.status.open", "Abierta");
      case 2:
        return t("incidence.status.closed", "Cerrada");
      case 3:
        return t("incidence.status.cancelled", "Cancelada");
      default:
        return "-";
    }
  };

  // Get status tag color
  const getStatusColor = (status: number): string => {
    switch (status) {
      case 1:
        return "blue"; // Open
      case 2:
        return "green"; // Closed
      case 3:
        return "red"; // Cancelled
      default:
        return "default";
    }
  };

  // Get priority display text (translated)
  const getPriorityDisplay = (priority: string): string => {
    switch (priority) {
      case "critical":
        return t("incidence.priority.critical", "Crítica");
      case "high":
        return t("incidence.priority.high", "Alta");
      case "medium":
        return t("incidence.priority.medium", "Media");
      case "low":
        return t("incidence.priority.low", "Baja");
      default:
        return "-";
    }
  };

  // Get priority tag color
  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case "critical":
        return "red";
      case "high":
        return "orange";
      case "medium":
        return "blue";
      case "low":
        return "default";
      default:
        return "default";
    }
  };

  const handleEdit = () => {
    if (record) {
      navigate(`/incidences/edit/${record.id}`);
    }
  };

  const handleChangeStatus = () => {
    setStatusModalVisible(true);
  };

  const handleModalClose = () => {
    setStatusModalVisible(false);
    // Refetch data after status change
    query.refetch();
  };

  return (
    <Show
      isLoading={isLoading}
      title={t("incidence.detail_title", "Incidence Detail")}
      headerButtons={() => (
        <Space>
          {record?.status_incidence === 1 && (
            <>
              <Button
                type="default"
                icon={<EditOutlined />}
                onClick={handleEdit}
              >
                {t("incidence.edit_incidence", "Editar")}
              </Button>
              <Button
                type="primary"
                danger
                icon={<CloseCircleOutlined />}
                onClick={handleChangeStatus}
              >
                {t("incidence.close_incidence", "Cerrar Incidencia")}
              </Button>
            </>
          )}
        </Space>
      )}
    >
      <Tabs defaultActiveKey="1">
        {/* Tab 1: General Information */}
        <Tabs.TabPane
          tab={t("incidence.general_info", "General Information")}
          key="1"
        >
          {/* Producer Summary Card */}
          {record?.audit_detail?.producer && (
            <Card
              title={t("incidence.producer_summary", "Producer Summary")}
              style={{ marginBottom: 24 }}
            >
              <Descriptions column={3} bordered>
                <Descriptions.Item
                  label={t("producer.name", "Producer Name")}
                  span={3}
                >
                  <strong>{record.audit_detail.producer.name}</strong>
                </Descriptions.Item>

                <Descriptions.Item
                  label={t("producer.country_portfolio", "Country/Portfolio")}
                  span={1}
                >
                  {record.audit_detail.producer.country || 
                   record.audit_detail.producer.portfolio_name ||
                   (record.audit_detail.producer.portfolio 
                     ? t("producer.portfolio_number", `Portfolio ${record.audit_detail.producer.portfolio}`)
                     : "-")}
                </Descriptions.Item>

                <Descriptions.Item
                  label={t("producer.main_contact", "Main Contact")}
                  span={2}
                >
                  {record.audit_detail.producer.main_contact ? (
                    <div>
                      {record.audit_detail.producer.main_contact.name && (
                        <div>{record.audit_detail.producer.main_contact.name}</div>
                      )}
                      {record.audit_detail.producer.main_contact.phone && (
                        <div style={{ fontSize: "12px" }}>
                          {t("common.phone", "Tel")}: {record.audit_detail.producer.main_contact.phone}
                        </div>
                      )}
                      {record.audit_detail.producer.main_contact.email && (
                        <div style={{ fontSize: "12px" }}>
                          {record.audit_detail.producer.main_contact.email}
                        </div>
                      )}
                    </div>
                  ) : (
                    "-"
                  )}
                </Descriptions.Item>

                <Descriptions.Item
                  label={t("producer.total_farms", "Number of Farms")}
                  span={1}
                >
                  {record.audit_detail.producer.total_farms || 0}
                  {record.audit_detail.producer.provinces && 
                   record.audit_detail.producer.provinces.length > 0 && (
                    <span style={{ color: "#666", marginLeft: "4px" }}>
                      ({record.audit_detail.producer.provinces.join(t("common.and", " and "))})
                    </span>
                  )}
                </Descriptions.Item>

                <Descriptions.Item
                  label={t("producer.total_surface", "Total Surface")}
                  span={1}
                >
                  {record.audit_detail.producer.total_surface
                    ? `${formatNumber(record.audit_detail.producer.total_surface)} ${t("common.ha", "ha")}`
                    : `0 ${t("common.ha", "ha")}`}
                </Descriptions.Item>

                <Descriptions.Item
                  label={t("producer.agent", "Assigned Agent")}
                  span={1}
                >
                  {record.audit_detail.producer.agent ? (
                    <div>
                      <div>{record.audit_detail.producer.agent.name || "-"}</div>
                      {record.audit_detail.producer.agent.email && (
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          {record.audit_detail.producer.agent.email}
                        </div>
                      )}
                    </div>
                  ) : (
                    "-"
                  )}
                </Descriptions.Item>

                {/* Other Incidences */}
                {record.audit_detail.producer.other_incidences && 
                 record.audit_detail.producer.other_incidences.length > 0 && (
                  <Descriptions.Item
                    label={t("producer.other_incidences", "Other Incidences")}
                    span={3}
                  >
                    <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                      {record.audit_detail.producer.other_incidences.map((inc) => (
                        <div
                          key={inc.id}
                          style={{
                            padding: "8px",
                            marginBottom: "8px",
                            backgroundColor: "#f5f5f5",
                            borderRadius: "4px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <a
                              href={`/incidences/${inc.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => {
                                e.preventDefault();
                                window.open(`/incidences/${inc.id}`, '_blank');
                              }}
                              style={{ fontWeight: 500, cursor: "pointer" }}
                            >
                              {inc.num_incidence}
                            </a>
                            {inc.kind_incidence_name && (
                              <span style={{ marginLeft: "8px", color: "#666" }}>
                                - {inc.kind_incidence_name}
                              </span>
                            )}
                            <div style={{ fontSize: "12px", color: "#999", marginTop: "2px" }}>
                              {inc.created && dayjs(inc.created).format("DD/MM/YYYY")}
                              {inc.assigned_to_name && (
                                <span style={{ marginLeft: "8px" }}>
                                  • {t("incidence.assigned_to_short", "Assigned to")}: {inc.assigned_to_name}
                                </span>
                              )}
                            </div>
                          </div>
                          <Tag
                            color={
                              inc.status_incidence === 1
                                ? "blue"
                                : inc.status_incidence === 2
                                ? "green"
                                : "red"
                            }
                          >
                            {getStatusDisplay(inc.status_incidence)}
                          </Tag>
                        </div>
                      ))}
                    </div>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          )}

          <Card title={t("incidence.details", "Incidence Details")}>
            <Descriptions column={3} bordered>
              <Descriptions.Item
                label={t("incidence.number", "Number")}
                span={1}
              >
                <strong>{record?.num_incidence}</strong>
              </Descriptions.Item>
              <Descriptions.Item
                label={t("incidence.status_label", "Status")}
                span={1}
              >
                {record && (
                  <Tag color={getStatusColor(record.status_incidence)}>
                    {getStatusDisplay(record.status_incidence)}
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("incidence.priority_label", "Priority")}
                span={1}
              >
                {record && (
                  <Tag color={getPriorityColor(record.priority)}>
                    {getPriorityDisplay(record.priority)}
                  </Tag>
                )}
              </Descriptions.Item>

              <Descriptions.Item
                label={t("incidence.created", "Creation Date")}
                span={1}
              >
                {record?.created
                  ? dayjs(record.created).format("DD/MM/YYYY HH:mm")
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("incidence.type", "Type")}
                span={1}
              >
                {record?.kind_incidence_detail?.name}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("incidence.deadline", "Deadline")}
                span={1}
              >
                {record?.deadline
                  ? dayjs(record.deadline).format("DD/MM/YYYY")
                  : t("incidence.no_deadline", "No deadline")}
              </Descriptions.Item>

              <Descriptions.Item
                label={t("incidence.reported_by", "Reported By")}
                span={1}
              >
                {record?.reported_by_detail?.full_name || "-"}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("incidence.assigned_to", "Assigned To")}
                span={1}
              >
                {record?.assigned_to_detail?.full_name ||
                  t("incidence.not_assigned", "Not assigned")}
              </Descriptions.Item>
              <Descriptions.Item
                label={t(
                  "incidence.requires_immediate_action",
                  "Requires Immediate Action"
                )}
                span={1}
              >
                <Tag color={record?.requires_immediate_action ? "red" : "default"}>
                  {record?.requires_immediate_action
                    ? t("common.yes", "Yes")
                    : t("common.no", "No")}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item
                label={t("incidence.description", "Description")}
                span={3}
              >
                {/* Mismo treatment que los comentarios: pre-wrap para
                    conservar saltos del usuario + wordBreak para que
                    URLs / códigos larguísimos no rompan el layout de
                    Descriptions cuando la columna es estrecha. */}
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {record?.description}
                </div>
              </Descriptions.Item>

              {/* Resolution information (only for closed/cancelled incidences) */}
              {record?.status_incidence !== 1 && (
                <>
                  <Descriptions.Item
                    label={t("incidence.resolved_date", "Resolution Date")}
                    span={1}
                  >
                    {record?.resolved_date
                      ? dayjs(record.resolved_date).format("DD/MM/YYYY")
                      : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t(
                      "incidence.regularized_surface",
                      "Regularized Surface"
                    )}
                    span={2}
                  >
                    {record?.regularized_surface 
                      ? `${record.regularized_surface} ${t("common.ha", "ha")}`
                      : "-"}
                  </Descriptions.Item>

                  <Descriptions.Item
                    label={t("incidence.resolution_notes", "Resolution Notes")}
                    span={3}
                  >
                    {record?.resolution_notes || "-"}
                  </Descriptions.Item>
                </>
              )}
            </Descriptions>
          </Card>
        </Tabs.TabPane>

        {/* Tab 2: Audit Information */}
        <Tabs.TabPane
          tab={t("incidence.audit_info", "Audit")}
          key="2"
        >
          {record?.audit_detail && (
            <AuditInfoPanel audit={record.audit_detail} />
          )}
        </Tabs.TabPane>

        {/* Tab 3: Comments */}
        <Tabs.TabPane
          tab={t("incidence.comments", "Comments")}
          key="3"
        >
          {record?.id && <IncidenceComments incidenceId={record.id} />}
        </Tabs.TabPane>
      </Tabs>

      {/* Change Status Modal */}
      {record && (
        <ChangeStatusModal
          visible={statusModalVisible}
          onCancel={handleModalClose}
          incidence={record}
        />
      )}
    </Show>
  );
};
